import { lazy, Suspense, useEffect, useRef, useState, type ReactElement } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation,
         useNavigate, useParams } from "react-router-dom";
import AcceptInvite from "./pages/AcceptInvite";
import Login from "./pages/Login";
import Organization from "./pages/Organization";
import Profile from "./pages/Profile";
import Dossier from "./pages/Dossier";
import Ansamblu from "./pages/Ansamblu";
import Incercari from "./pages/Incercari";
import Excavatie from "./pages/Excavatie";
import Pud from "./pages/Pud";
import Studio from "./pages/Studio";
import Verificari from "./pages/Verificari";
import Colectie from "./pages/Colectie";
import Editor from "./pages/Editor";
import Costuri from "./pages/Costuri";
import Macheta from "./pages/Macheta";
import StructuraFatada from "./fatadaStructura/Pagina";
import Localizare from "./pages/Localizare";
import TemaProiect from "./pages/TemaProiect";
import Fatada from "./pages/Fatada";
import Volumetrie from "./pages/Volumetrie";
import Projects from "./pages/Projects";
import ProiectNou from "./pages/ProiectNou";
import Setari from "./setari/Setari";
import Toasts from "./components/Toasts";
import InterfataCazuta from "./components/InterfataCazuta";
import { Logo } from "./components/ui";
import MeniuCont from "./components/MeniuCont";
import NumeProiect from "./components/NumeProiect";
import { potPleca } from "./nescris";
import { RolProiectProvider } from "./components/rol";
import { SessionProvider, useSession } from "./session";
import { LimbaProvider, useT, useTn } from "./i18n";
import { listJobs, logPageView, type Job } from "./api";
import { StructureProvider } from "./structure/StructureContext";
import StructureStepRouter from "./structure/StructureStepRouter";
import ActivitateCentru from "./structure/ActivitateCentru";
import { useCautaLaCapatulCitirii } from "./viewer/cost/piata";
import ObsHost from "./structure/ObsHost";
import {
  IcBranch, IcDosar, IcPreferinte, IcStudio, IcVerificari,
} from "./structure/icoane";
import { RegistruProvider, useRegistru } from "./nav/Registru";
import Lant from "./nav/Lant";
import { EroareRegistru } from "./nav/BlocSolutie";
import { amintesteRuta, ultimaRuta, useFir } from "./nav/useFir";
import { caleEtapa, caleSolutii, caleVerificari, caleaDeStart,
         picateGlobaleRand } from "./nav/selectie";

const DwgViewer = lazy(() => import("./structure/DwgViewer"));

// Poarta rutelor private. `loading` NU redirectează: altfel un refresh pe
// /proiecte ar arunca userul la login înainte ca /api/auth/me să răspundă.
function Private({ children }: { children: ReactElement }) {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) return <SeIncarca />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

// ȘINA PROIECTULUI (A2): Proiect, Soluții, Dosar, Verificări, Module, Setări.
//
// «Proiect» e vizualizatorul — ultima soluție privită, altfel firul implicit
// (v. `StartProiect`). Pipeline-ul, setările de calcul și pastila soluției au
// PLECAT din șină: lanțul și setările de calcul sunt vederi ale structurii
// («Lanțul de calcul», «Setări de calcul» în meniul ei), iar pe ce soluție
// ești o spune lanțul din antet, cu numele ei. Costurile sunt al patrulea
// segment al lanțului.
//
// Roata dințată rămâne singură jos, după un distanțier: setările PROIECTULUI
// (acces, nume, arhivare) nu sunt un pas al lucrului, ci ce e în jurul lui.
export function Sina({ projectId }: { projectId: string }) {
  const t = useT();
  const tn = useTn();
  const path = useLocation().pathname;
  const { ruta, fir, rand } = useFir();
  const base = `/p/${projectId}`;
  const on = {
    proiect: ["situatie", "arhitectura", "structura", "costuri"].includes(ruta.pagina)
      || path.startsWith(`${base}/incercari`) || path.startsWith(`${base}/excavatie`),
    solutii: ruta.pagina === "solutii",
    dosar: path.startsWith(`${base}/dossier`) || path.startsWith(`${base}/location`)
      || path.startsWith(`${base}/brief`) || path.startsWith(`${base}/volumetrie`)
      || path.startsWith(`${base}/facade`),
    verificari: ruta.pagina === "verificari",
    setari: path.startsWith(`${base}/settings`),
  };
  // Verificările rândului privit; de pe altă pagină, ale firului — structura
  // întâi, fiindcă ea poartă cele mai multe verdicte.
  const deVerificat = rand?.id ?? fir.structure ?? fir.architecture ?? fir.site_plan;
  // BULINA: câte critice și globale pică pe rândul ăsta, pe TOATE corpurile
  // lui. E singurul loc care spune că o soluție de structură nu trece — lângă
  // soluție nu se mai scrie (v. `selectie.faraVerdict`); cifra fiecărui corp
  // stă pe butonul lui din grupul «Corp» al Studioului.
  const { registru } = useRegistru();
  const picate = picateGlobaleRand(
    registru?.solutii.find((s) => s.id === deVerificat)) ?? 0;
  // De pe o subpagină a structurii (pasul, elementul, marca), «Proiect» duce
  // la Studioul rândului privit — modelul 3D —, nu la ultima rută ținută minte.
  const laProiect = ruta.pagina === "structura" && ruta.sub && ruta.sol
    ? caleEtapa(projectId, "structure", ruta.sol, null, { corp: ruta.corp })
    : base;
  return (
    <nav className="ob-rail" aria-label={t("rail.aria")}>
      <a className={`ob-rit${on.proiect ? " on" : ""}`} title={t("rail.proiect")}
         aria-label={t("rail.proiect")} href={laProiect}><IcStudio /></a>
      <a className={`ob-rit${on.solutii ? " on" : ""}`} title={t("rail.solutii")}
         aria-label={t("rail.solutii")} href={caleSolutii(projectId)}><IcBranch /></a>
      <a className={`ob-rit${on.dosar ? " on" : ""}`} title={t("rail.dosar")}
         aria-label={t("rail.dosar")} href={`${base}/dossier`}><IcDosar /></a>
      <a className={`ob-rit${on.verificari ? " on" : ""}`}
         title={picate ? tn("rail.verificari_picate#n", picate) : t("rail.verificari")}
         aria-label={picate ? tn("rail.verificari_picate#n", picate) : t("rail.verificari")}
         href={caleVerificari(projectId, deVerificat)}>
        <IcVerificari />
        {picate > 0 && (
          <span className="ob-rit-bulina" aria-hidden="true">{picate > 99 ? "99+" : picate}</span>
        )}
      </a>
      {/* COLECȚIA A PLECAT DIN ȘINĂ. Modulele sunt ale omului, nu ale
          proiectului (migrarea 0045): se ajunge la ele din meniul contului,
          de oriunde, iar un proiect nu mai are o colecție a lui pe care s-o
          arate aici. */}
      <div className="ob-rail-sp" />
      <a className={`ob-rit${on.setari ? " on" : ""}`} title={t("rail.setari")}
         aria-label={t("rail.setari")} href={`${base}/settings`}><IcPreferinte /></a>
    </nav>
  );
}

// The rail's anchors are plain hrefs; intercept them for SPA navigation, same
// trick as ObsHost.
function RailHost({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: "contents" }}
         onClick={(e) => {
           if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
           const a = (e.target as HTMLElement).closest("a");
           const href = a?.getAttribute("href");
           if (!href?.startsWith("/")) return;
           e.preventDefault();
           // ÎNTÂI ÎNTREBAREA ECRANULUI, dacă are una: railul e drumul pe care
           // se pleacă dintr-un editor cu lucru nesalvat, iar `beforeunload` nu
           // vede navigarea din aplicație (v. `nescris.ts`).
           if (!potPleca()) return;
           navigate(href);
         }}>
      <Sina projectId={projectId} />
    </div>
  );
}

// ⚡ AL PLATFORMEI (A9): ce se lucrează acum, pe etape, din registru; plus
// rulările care nu nasc soluții (citirea actelor, importul planșelor), din
// `listJobs`, reîmprospătate la ~4 s cât rulează ceva.
function ActivityCenterHost({ projectId }: { projectId: string }) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const anyAlive = jobs.some((j) => j.status === "running" || j.status === "pending");
  useCautaLaCapatulCitirii(projectId, jobs);
  useEffect(() => {
    let alive = true;
    const take = () => {
      if (document.hidden) return;
      listJobs(projectId).then((j) => alive && setJobs(j)).catch(() => {});
    };
    take();
    const t = setInterval(take, anyAlive ? 4000 : 10000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [projectId, anyAlive]);
  return <ActivitateCentru id={projectId} jobs={jobs} />;
}

/** Ține minte ultima rută privită a vizualizatorului, ca `/p/:id` să se
 *  deschidă acolo (A4). Numai rutele cu un rând în cale: o pagină de listă nu
 *  e «unde ai rămas». */
function JurnalFir({ projectId }: { projectId: string }) {
  const { pathname, search } = useLocation();
  const { ruta, rand } = useFir();
  useEffect(() => {
    if (!rand) return;
    if (ruta.pagina !== "situatie" && ruta.pagina !== "arhitectura"
        && ruta.pagina !== "structura") return;
    // o SUBPAGINĂ (pasul, elementul, marca) nu e «unde ai rămas» în
    // vizualizator: ținută minte, «Proiect» din șină ducea înapoi pe ea
    if (ruta.sub) return;
    amintesteRuta(projectId, `${pathname}${search}`);
  }, [projectId, pathname, search, ruta.pagina, rand]);
  return null;
}

/** `/p/:id`: ultima rută privită (per vizitator), altfel firul implicit
 *  (contract §8). Așteaptă registrul — fără el nu se știe ce e firul —, iar
 *  dacă n-a putut fi citit, o spune, cu «Încearcă din nou». */
function StartProiect() {
  const { projectId = "" } = useParams();
  const { registru, eroare } = useRegistru();
  if (!registru && eroare) {
    return <div className="studio"><EroareRegistru motiv={eroare} /></div>;
  }
  if (!registru) return <SeIncarca />;
  return <Navigate replace to={caleaDeStart(registru, projectId, ultimaRuta(projectId))} />;
}

function Shell({ children }: { children: ReactElement }) {
  const path = useLocation().pathname;
  // Railul apare doar ÎN interiorul unui proiect: în afara lui n-ar avea la ce
  // să ducă, iar un rail cu toate butoanele moarte e mai rău decât niciunul.
  const proiect = path.match(/^\/p\/([^/]+)/)?.[1];
  const top = (
    <div className="top">
      <Link to="/projects" style={{ color: "inherit" }}
            onClick={(e) => { if (!potPleca()) e.preventDefault(); }}>
        <Logo />
      </Link>
      {proiect && <NumeProiect id={proiect} />}
      {/* LANȚUL: pe ce fir ești și câte soluții are fiecare etapă (A3) */}
      {proiect && <Lant />}
      <div className="sp" />
      {/* the activity centre portals itself into this slot (see
          ActivitateCentru) — the header is the one thing always on screen */}
      <div className="ob-antet-stare" id="antet-stare" />
      <MeniuCont />
    </div>
  );
  // Structura e cea din temă: `.screen` e coloana de înălțime fixă, `.body` e
  // ce rămâne sub bara de sus, iar `.main` e zona care se derulează. Contează
  // pentru mai mult decât aspectul: ecranele cu hartă își cer înălțimea în
  // procente, iar într-un părinte fără înălțime definită Leaflet se
  // inițializează cu dimensiune zero și încadrează parcela la zoom de planetă.
  if (!proiect) {
    return (
      <div className="screen">
        {top}
        <div className="body">
          <div className="main">{children}</div>
        </div>
      </div>
    );
  }
  // În proiect, totul stă sub registrul soluțiilor (lanțul, șina, ecranele)
  // și sub furnizorul structurii, care citește din el rândul privit.
  return (
    <div className="screen">
      {/* Rolul pe proiect, cerut o dată pentru toate ecranele de dedesubt:
          după el își sting singure butoanele pe care serverul le-ar refuza. */}
      <RolProiectProvider projectId={proiect}>
      <RegistruProvider projectId={proiect}>
      <StructureProvider projectId={proiect}>
        {top}
        <div className="body">
          <RailHost projectId={proiect} />
          <div className="main">{children}</div>
        </div>
        <ActivityCenterHost projectId={proiect} />
        <JurnalFir projectId={proiect} />
      </StructureProvider>
      </RegistruProvider>
      </RolProiectProvider>
    </div>
  );
}

/** Vechea rută a editorului pe proiect -> aceeași planșă, în colecția omului.
 *  Codul e același; doar de sub ce stă s-a schimbat. */
function RedirectEditorModul() {
  const { code = "" } = useParams();
  return <Navigate replace to={`/tronsoane/${encodeURIComponent(code)}/edit`} />;
}

// Route hosts for the pages that are not step sections.
function EditorPage() {
  return (
    <ObsHost className="ob-main ob-main-plin">
      <Editor />
    </ObsHost>
  );
}

function DwgViewerPage() {
  const { projectId = "", fileId = "" } = useParams();
  return (
    <ObsHost className="ob-main ob-main-plin">
      <Suspense fallback={<SeIncarcaViewerul />}>
        <DwgViewer projectId={projectId} fileId={fileId} />
      </Suspense>
    </ObsHost>
  );
}

// Jurnalul de acces: o linie în `core.activity_log` la fiecare pagină deschisă.
//
// AICI, nu pe server, fiindcă interfața e un SPA: după prima încărcare
// navigarea se face în browser și serverul nu mai vede nimic — un `after_request`
// în control plane ar jurnaliza apeluri de API (inclusiv polling-ul la o
// jumătate de secundă), nu ecrane. React-router e singurul care știe ce pagină
// s-a deschis.
//
// Se trimite DOAR calea. Emailul și IP-ul le pune serverul, din cookie-ul
// semnat și din antetele lui nginx: un client care și-ar spune singur emailul
// n-ar fi o măsurătoare, ar fi o declarație.
function JurnalPagini() {
  const { pathname } = useLocation();
  const ultima = useRef<string | null>(null);
  useEffect(() => {
    // În dev, StrictMode montează efectul de două ori; și un `?corp=` care
    // se schimbă re-randează fără să schimbe pagina. Ambele ar dubla rândul.
    if (ultima.current === pathname) return;
    ultima.current = pathname;
    logPageView(pathname);
  }, [pathname]);
  return null;
}

/** Rândul «Se încarcă…», cât `/api/auth/me` nu a răspuns. Componentă, nu text
 *  în loc: are nevoie de `useT`, iar poarta rutelor private nu e o componentă
 *  de care să atârne un hook pe o cale de ieșire timpurie. */
function SeIncarca() {
  const t = useT();
  return <div className="page"><p className="note">{t("comun.se_incarca")}</p></div>;
}

function SeIncarcaViewerul() {
  const t = useT();
  return <div className="ob-pagina sg-muted">{t("app.se_incarca_viewerul")}</div>;
}

/** Limba vine din PROFIL, deci providerul ei stă sub cel de sesiune. */
function LimbaDinProfil({ children }: { children: ReactElement }) {
  const { user } = useSession();
  return <LimbaProvider limbaProfilului={user?.language}>{children}</LimbaProvider>;
}

export default function App() {
  // `.ob` e rădăcina temei: în `theme.css` TOTUL stă sub ea, inclusiv
  // variabilele de culoare. Fără clasa asta pe un strămoș, ecranele rămân
  // nestilate.
  return (
    <div className="ob">
     <InterfataCazuta>
     <SessionProvider>
      <LimbaDinProfil>
      <BrowserRouter>
        <JurnalPagini />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invitation/:token" element={<AcceptInvite />} />
          <Route path="/projects" element={<Private><Shell><Projects /></Shell></Private>} />
          {/* PROIECTUL NOU: tipul, apoi numele — rândul se scrie abia la capăt */}
          <Route path="/projects/new" element={<Private><Shell><ProiectNou /></Shell></Private>} />
          <Route path="/projects/new/:tip"
                 element={<Private><Shell><ProiectNou /></Shell></Private>} />
          {/* la structură, a doua alegere: de unde vine geometria macro */}
          <Route path="/projects/new/:tip/:geometrie"
                 element={<Private><Shell><ProiectNou /></Shell></Private>} />
          <Route path="/organization" element={<Private><Shell><Organization /></Shell></Private>} />
          <Route path="/profile" element={<Private><Shell><Profile /></Shell></Private>} />
          {/* `/p/:id`: ultima rută privită, altfel firul implicit */}
          <Route path="/p/:projectId"
                 element={<Private><Shell><StartProiect /></Shell></Private>} />
          <Route path="/p/:projectId/dossier"
                 element={<Private><Shell><Dossier /></Shell></Private>} />
          <Route path="/p/:projectId/dossier/dwg/:fileId"
                 element={<Private><Shell><DwgViewerPage /></Shell></Private>} />
          <Route path="/p/:projectId/location"
                 element={<Private><Shell><Localizare /></Shell></Private>} />
          <Route path="/p/:projectId/brief"
                 element={<Private><Shell><TemaProiect /></Shell></Private>} />
          <Route path="/p/:projectId/volumetrie"
                 element={<Private><Shell><Volumetrie /></Shell></Private>} />
          <Route path="/p/:projectId/facade"
                 element={<Private><Shell><Fatada /></Shell></Private>} />
          {/* SOLUȚIILE PROIECTULUI: arborele întreg, cu previzualizare (A1) */}
          <Route path="/p/:projectId/solutii"
                 element={<Private><Shell><Ansamblu /></Shell></Private>} />
          {/* VIZUALIZATORUL, câte o rută pe disciplină, cu un singur id în
              cale — rândul privit (contract §8). Fără id: segmentul gol,
              `?pe=<amonte>`, rezolvat pe soluția implicită de pe amonte. */}
          <Route path="/p/:projectId/situatie"
                 element={<Private><Shell><Studio disciplina="site_plan" /></Shell></Private>} />
          <Route path="/p/:projectId/situatie/:sol"
                 element={<Private><Shell><Studio disciplina="site_plan" /></Shell></Private>} />
          <Route path="/p/:projectId/arhitectura"
                 element={<Private><Shell><Studio disciplina="architecture" /></Shell></Private>} />
          <Route path="/p/:projectId/arhitectura/:sol"
                 element={<Private><Shell><Studio disciplina="architecture" /></Shell></Private>} />
          <Route path="/p/:projectId/structura"
                 element={<Private><Shell><Studio disciplina="structure" /></Shell></Private>} />
          <Route path="/p/:projectId/structura/:sol"
                 element={<Private><Shell><Studio disciplina="structure" /></Shell></Private>} />
          {/* paginile unui pas și fișa unui element, pe rândul din cale;
              `:section` e cheia de azi din `StructureStepRouter` */}
          <Route path="/p/:projectId/structura/:sol/pas/:section"
                 element={<Private><Shell><StructureStepRouter /></Shell></Private>} />
          <Route path="/p/:projectId/structura/:sol/element/:ref"
                 element={<Private><Shell><StructureStepRouter section="element" /></Shell></Private>} />
          {/* MACHETA UNIFICATĂ a rândului: situația cu fațade, cu structura
              în ea ca strat (`viewer/macheta`) */}
          <Route path="/p/:projectId/structura/:sol/macheta"
                 element={<Private><Shell><Macheta /></Shell></Private>} />
          {/* pagina unei mărci de bară / etrier («3g», «e1») a rândului */}
          <Route path="/p/:projectId/structura/:sol/armatura/:marca"
                 element={<Private><Shell><StructureStepRouter section="marca" /></Shell></Private>} />
          {/* depanarea subsolului unei arhitecturi: încercările ei, pașii
              împachetării, zonele fără locuri — din meniul arhitecturii */}
          <Route path="/p/:projectId/incercari/:solutionId"
                 element={<Private><Shell><Incercari /></Shell></Private>} />
          {/* ce a mutat pasul 1 din pereții excavației, și de ce: cele două
              contururi, cotele pe laturi, ce s-a respins */}
          <Route path="/p/:projectId/excavatie/:solutionId"
                 element={<Private><Shell><Excavatie /></Shell></Private>} />
          {/* exportul PUD de pe un plan de situație (v. `exporter/`) */}
          <Route path="/p/:projectId/pud/:solutionId"
                 element={<Private><Shell><Pud /></Shell></Private>} />
          {/* verificările rândului; fără id, ale firului */}
          <Route path="/p/:projectId/verificari"
                 element={<Private><Shell><Verificari /></Shell></Private>} />
          <Route path="/p/:projectId/verificari/:sol"
                 element={<Private><Shell><Verificari /></Shell></Private>} />
          <Route path="/p/:projectId/verificari/:sol/tip/:tip"
                 element={<Private><Shell><StructureStepRouter section="verificare" /></Shell></Private>} />
          {/* STRUCTURA FAȚADEI unei arhitecturi: vântul pe anvelopă și cortina
              (`fatadaStructura/`) — separată de Studio-ul structurii */}
          <Route path="/p/:projectId/fatada-structura/:sol"
                 element={<Private><Shell><StructuraFatada /></Shell></Private>} />
          {/* devizul unui calcul de detaliu; fără id, al firului */}
          <Route path="/p/:projectId/costuri"
                 element={<Private><Shell><Costuri /></Shell></Private>} />
          <Route path="/p/:projectId/costuri/:sol"
                 element={<Private><Shell><Costuri /></Shell></Private>} />
          {/* COLECȚIA OMULUI — aceeași pagină, fără niciun proiect în cale.
              Modulele sunt ale celui care le desenează (migrarea 0045), deci
              se ajunge la ele din meniul contului. */}
          <Route path="/tronsoane"
                 element={<Private><Shell><Colectie /></Shell></Private>} />
          <Route path="/tronsoane/:code/edit"
                 element={<Private><Shell><EditorPage /></Shell></Private>} />
          {/* Vechile rute ale colecției pe proiect duc la colecția omului:
              linkuri date mai departe și file lăsate deschise nu au de ce să
              cadă în gol. */}
          <Route path="/p/:projectId/templates"
                 element={<Navigate replace to="/tronsoane" />} />
          <Route path="/p/:projectId/templates/:code/edit"
                 element={<RedirectEditorModul />} />
          {/* Setările proiectului: o pagină cu secțiuni (General, Acces).
              Ruta fără secțiune o deschide pe prima. Setările de CALCUL ale
              structurii sunt o vedere a ei (`?vedere=setari`). */}
          <Route path="/p/:projectId/settings"
                 element={<Private><Shell><Setari /></Shell></Private>} />
          <Route path="/p/:projectId/settings/:sectiune"
                 element={<Private><Shell><Setari /></Shell></Private>} />
          {/* Rutele vechi (`/studio`, `/solutions`, `/structure/step/…`,
              `/checks`, `/costs`, `/preferences`) nu se mai recunosc și nu
              se redirecționează: nu păstrăm compatibilitatea înapoi. */}
          {/* `/generation` și `/architecture` au fost SCOASE pe main
              (31f3666): generarea se cere azi din coloanele ecranului Soluții,
              deci redirecționările nu mai duceau nicăieri util. Ce nu se
              potrivește oricum cade pe `*`, adică pe lista de proiecte. */}
          <Route path="*" element={<Navigate to="/projects" replace />} />
        </Routes>
      </BrowserRouter>
      </LimbaDinProfil>
      <Toasts />
     </SessionProvider>
     </InterfataCazuta>
    </div>
  );
}
